import { useEffect, useState } from 'react';
import { Languages, MonitorPlay, Square, Wand2 } from 'lucide-react';
import { api } from '../lib/api';
import { useMediaList } from '../lib/live';
import { toast, useSession } from '../lib/session';
import type { Media } from '../lib/types';
import { ModalTitle } from '../ui';
import { TapTwiceButton } from '../ui/tapTwice';

// Drive → Encoding & audio: nothing is encoded automatically. A film plays from its original
// file; here the host (or the uploader) picks extra qualities and other audio languages, and
// starts or stops the job. Unticking something that exists deletes it.

const QUALITIES = [2160, 1440, 1080, 720, 480];
const qualityName = (q: number) => (q >= 2160 ? '4K' : `${q}p`);

/** The film's resolution class, as the server counts it (a 1920×800 film is 1080p). */
function classOf(w: number, h: number): number {
  const c = Math.max(h, Math.round((w * 9) / 16));
  return [2160, 1440, 1080, 720, 480, 360].find((std) => c >= (std * 92) / 100) ?? c;
}

export function EncodeDialog({ m }: { m: Media }) {
  const refresh = useMediaList((s) => s.refresh);
  const top = classOf(m.width, m.height);
  const made = new Set((m.renditions ?? []).filter((r) => !r.copy).map((r) => Number.parseInt(r.name, 10)));
  const prepared = new Set((m.audioTracks ?? []).map((a) => a.id).filter(Boolean));
  // A CPU-only server makes versions up to 1080p; 4K/1440p need a hardware encoder (a Mac host).
  const limit = useSession((s) => s.config?.encodeLimit) || 2160;
  const [qualities, setQualities] = useState<Set<number>>(() => new Set((m.encodePlan?.qualities ?? [...made]).filter((q) => q <= limit || made.has(q))));
  const [audio, setAudio] = useState<Set<string>>(() => new Set(m.encodePlan?.audio ?? [...prepared]));
  const [busy, setBusy] = useState(false);
  const running = !!m.encodePlan?.pending;
  // Follow the job while it runs (the library list otherwise refreshes only now and then).
  useEffect(() => {
    if (!running) return;
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
  }, [running, refresh]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast(ok, 'accent');
      refresh();
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(false);
    }
  };
  const changed = sameSet(qualities, made) === false || sameSet(audio, prepared) === false;
  // Without the original, versions are made from the streaming copy, but it carries only the main
  // language: a language that isn't ready yet can't be added any more.
  const needsOriginal = m.originalRemoved && [...audio].some((a) => !prepared.has(a));

  return (
    <>
      <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Wand2 size={20} /> Encoding &amp; audio
      </ModalTitle>
      <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
        {m.title} plays from its {m.originalRemoved ? 'streaming copy' : 'original file'}{top ? ` (${qualityName(top)})` : ''}. Add smaller versions only for slow connections or browsers that can’t play
        the original; each takes the server roughly as long as the film runs.
      </p>
      <QualityChoices top={top} limit={limit} made={made} chosen={qualities} onChange={setQualities} disabled={busy} />
      <AudioChoices m={m} prepared={prepared} chosen={audio} onChange={setAudio} disabled={busy} />
      {running && <EncodeProgress stage={m.stage} progress={m.progress ?? 0} eta={m.eta ?? 0} />}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        {running ? (
          <TapTwiceButton className="btn btn-ghost" disabled={busy} armedText="Tap again to stop (progress is lost)" onConfirm={() => run(() => api.stopEncode(m.id), 'Encoding stopped: what finished is kept')}>
            <Square size={14} /> Stop encoding
          </TapTwiceButton>
        ) : (
          <button
            className="btn btn-black"
            disabled={busy || !changed || needsOriginal}
            title={needsOriginal ? 'The original file was removed, so other languages can’t be added' : undefined}
            onClick={() =>
              run(() => api.encodePlan(m.id, { qualities: [...qualities], audio: [...audio], start: true }), 'Started: progress shows here and in the Drive list')
            }
          >
            <Wand2 size={14} /> Start encoding
          </button>
        )}
      </div>
    </>
  );
}

const sameSet = <T,>(a: Set<T>, b: Set<T>) => a.size === b.size && [...a].every((x) => b.has(x));
const toggled = <T,>(set: Set<T>, v: T) => {
  const next = new Set(set);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  return next;
};

function QualityChoices({
  top,
  limit,
  made,
  chosen,
  onChange,
  disabled,
}: {
  top: number;
  limit: number;
  made: Set<number>;
  chosen: Set<number>;
  onChange: (s: Set<number>) => void;
  disabled: boolean;
}) {
  const options = QUALITIES.filter((q) => q <= top);
  const tooBig = (q: number) => q > limit && !made.has(q);
  const status = (q: number) => {
    if (made.has(q)) return 'made';
    if (tooBig(q)) return 'too slow on this server';
    return q === top ? 'same size, H.264' : 'not made';
  };
  return (
    <div className="enc-section">
      <div className="enc-head">
        <MonitorPlay size={15} /> Extra qualities
      </div>
      {options.length === 0 && <div className="muted" style={{ fontSize: 12.5 }}>The original is already the smallest size.</div>}
      {options.some(tooBig) && (
        <div className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          This server has no video chip, so making a {limit < 1440 ? '1440p or 4K' : '4K'} version would take it about a day per film; it makes versions up to {limit}p. Running Video Bridge on a Mac lifts this (Macs encode 4K on their video chip). The original still plays at full quality.
        </div>
      )}
      <div className="enc-grid">
        {options.map((q) => (
          <label key={q} className={`enc-opt${chosen.has(q) ? ' on' : ''}${tooBig(q) ? ' off' : ''}`} title={tooBig(q) ? `No video chip on this server: it makes versions up to ${limit}p` : undefined}>
            <input type="checkbox" checked={chosen.has(q)} onChange={() => onChange(toggled(chosen, q))} disabled={disabled || tooBig(q)} />
            <span>{qualityName(q)}</span>
            <small>{status(q)}</small>
          </label>
        ))}
      </div>
    </div>
  );
}

function AudioChoices({ m, prepared, chosen, onChange, disabled }: { m: Media; prepared: Set<string>; chosen: Set<string>; onChange: (s: Set<string>) => void; disabled: boolean }) {
  const others = (m.sourceAudio ?? []).slice(1);
  const first = m.sourceAudio?.[0];
  let empty = '';
  const locked = (id: string) => !!m.originalRemoved && !prepared.has(id);
  if (!m.sourceAudio) empty = m.originalRemoved ? 'The original file was removed, so its other languages can’t be added.' : 'Checking the file’s audio tracks…';
  else if (others.length === 0) empty = 'This film has one audio language.';
  return (
    <div className="enc-section">
      <div className="enc-head">
        <Languages size={15} /> Audio languages
      </div>
      {first && (
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
          {first.label} plays with the video. Pick the others to offer in the player (Subtitles → Audio).
        </div>
      )}
      {empty && <div className="muted" style={{ fontSize: 12.5 }}>{empty}</div>}
      {m.originalRemoved && others.some((a) => locked(a.id)) && (
        <div className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          The original file was removed to save space, and the streaming copy keeps only {first?.label ?? 'the main language'}.
        </div>
      )}
      <div className="enc-grid">
        {others.map((a) => (
          <label key={a.id} className={`enc-opt${chosen.has(a.id) ? ' on' : ''}${locked(a.id) ? ' off' : ''}`}>
            <input type="checkbox" checked={chosen.has(a.id)} onChange={() => onChange(toggled(chosen, a.id))} disabled={disabled || locked(a.id)} />
            <span className="truncate">{a.label}</span>
            <small>{prepared.has(a.id) ? 'ready' : locked(a.id) ? 'needs the original' : 'not ready'}</small>
          </label>
        ))}
      </div>
    </div>
  );
}

function EncodeProgress({ stage, progress, eta }: { stage: string; progress: number; eta: number }) {
  const pct = Math.round(progress * 100);
  const left = eta > 90 ? ` · ${Math.round(eta / 60)} min left` : eta > 0 ? ` · ${eta} s left` : '';
  return (
    <div className="enc-progress">
      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
        <span>{stage}</span>
        <span className="mono">
          {pct}%{left}
        </span>
      </div>
      <div className="progress accent">
        <div style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
